import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowRight, ArrowUp, Building2, Clock3, MessageSquare, RefreshCw, Scale, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { leaderboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getLogoOrInitials } from '../utils/companyLogo';
import { getErrorMessage } from '../lib/utils';

const CATEGORIES = [
  { id: 'quotation_value', label: 'Quotation Value', icon: Trophy },
  { id: 'freight_weight', label: 'Freight Weight', icon: Scale },
  { id: 'response_time', label: 'Average Response Time', icon: Clock3 },
];

const PERIODS = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Yearly' },
  { id: 'all_time', label: 'All Time' },
];

const rankClasses = {
  1: 'border-yellow-400 bg-yellow-50 text-yellow-800',
  2: 'border-gray-400 bg-gray-50 text-gray-700',
  3: 'border-amber-600 bg-amber-50 text-amber-800',
};

const MemberLeaderboard = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [category, setCategory] = useState('quotation_value');
  const [period, setPeriod] = useState('monthly');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadLeaderboard = useCallback(async ({ background = false } = {}) => {
    try {
      if (!background) setLoading(true);
      const response = await leaderboardApi.getLeaderboard(category, period);
      setPayload(response.data);
    } catch (error) {
      if (!background) {
        toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to load leaderboard'));
      }
    } finally {
      if (!background) setLoading(false);
    }
  }, [category, period]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  useEffect(() => {
    const refreshSeconds = Math.max(
      30,
      Number(payload?.auto_refresh_seconds || 60)
    );
    const timer = window.setInterval(
      () => loadLeaderboard({ background: true }),
      refreshSeconds * 1000
    );
    return () => window.clearInterval(timer);
  }, [loadLeaderboard, payload?.auto_refresh_seconds]);

  const selectedCategory = useMemo(
    () => CATEGORIES.find((item) => item.id === category) || CATEGORIES[0],
    [category],
  );
  const selectedPeriod = useMemo(
    () => PERIODS.find((item) => item.id === period) || PERIODS[1],
    [period],
  );

  const formatMetric = (row) => {
    if (category === 'quotation_value') {
      return `${payload?.standard_currency || 'USD'} ${Number(row.quotation_value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    if (category === 'freight_weight') {
      return `${Number(row.freight_weight || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
    }
    return row.qualified ? row.response_display : 'Not enough activity to rank';
  };

  const openProfile = (row) => {
    if (row.company_id === member?.company_id) {
      navigate('/profile');
      return;
    }
    navigate('/directory', { state: { openMemberId: row.profile_member_id } });
  };

  const openMessage = (row) => {
    navigate('/chat', {
      state: {
        openChatWith: {
          member_id: row.profile_member_id,
          company_name: row.company_name,
          country: row.country,
          tier: row.tier,
          company_logo: row.company_logo,
        },
      },
    });
  };

  const rows = payload?.results || [];
  const yourRanking = payload?.your_ranking;
  const metrics = payload?.metrics;

  const formatSummaryMetric = (metricCategory) => {
    const metric = metrics?.[metricCategory];
    if (!metric?.available) return 'No eligible quotations';
    if (metricCategory === 'quotation_value') {
      return `${payload?.standard_currency || 'USD'} ${Number(metric.quotation_value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
    if (metricCategory === 'freight_weight') {
      return `${Number(metric.freight_weight || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
    }
    return metric.response_display || 'No eligible quotations';
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="member-leaderboard">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Member Leaderboard</h1>
          <p className="mt-1 text-slate-600">Compare company quotation activity across separate performance measures.</p>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>Last updated {payload?.updated_at ? new Date(payload.updated_at).toLocaleString() : 'not available'}</span>
          <span className="hidden sm:inline">Refreshes automatically</span>
          <Button variant="outline" size="sm" onClick={() => loadLeaderboard()} disabled={loading} title="Refresh leaderboard">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {payload?.full_access && metrics && (
        <section aria-label="Your valid quotation activity">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Your valid quotation activity</h2>
              <p className="text-sm text-slate-600">{selectedPeriod.label} figures include only eligible quotations.</p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="border border-slate-200 bg-white p-4">
              <p className="text-sm font-medium text-slate-600">Quotation Value</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{formatSummaryMetric('quotation_value')}</p>
              <p className="mt-1 text-xs text-slate-500">{metrics.quotation_value?.eligible_quotations || 0} eligible quotations</p>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <p className="text-sm font-medium text-slate-600">Total Weight Quoted</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{formatSummaryMetric('freight_weight')}</p>
              <p className="mt-1 text-xs text-slate-500">{metrics.freight_weight?.eligible_quotations || 0} eligible quotations</p>
            </div>
            <div className="border border-slate-200 bg-white p-4">
              <p className="text-sm font-medium text-slate-600">Average Response Time</p>
              <p className="mt-2 text-2xl font-bold text-slate-900">{formatSummaryMetric('response_time')}</p>
              <p className="mt-1 text-xs text-slate-500">
                {metrics.response_time?.eligible_quotations || 0} eligible quotations
                {metrics.response_time?.available && !metrics.response_time?.qualified
                  ? ' · Not enough activity to rank'
                  : ''}
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="space-y-3 border-y border-slate-200 py-4">
        <div className="flex flex-wrap gap-2" aria-label="Leaderboard category">
          {CATEGORIES.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategory(item.id)}
                className={`inline-flex h-10 items-center gap-2 rounded-md border px-4 text-sm font-medium transition-colors ${category === item.id ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                <Icon size={16} />
                {item.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-1" aria-label="Leaderboard period">
          {PERIODS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setPeriod(item.id)}
              className={`h-9 border-b-2 px-4 text-sm font-medium transition-colors ${period === item.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {yourRanking && (
        <section className="flex flex-col gap-3 border-l-4 border-blue-600 bg-blue-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid="your-ranking">
          <div>
            <p className="text-xs font-semibold uppercase text-blue-700">Your Ranking</p>
            <p className="mt-1 font-semibold text-slate-900">
              {yourRanking.rank
                ? `${member?.company_name || 'Your company'} is ranked #${yourRanking.rank} for ${selectedCategory.label}.`
                : 'Not enough activity to rank in this period.'}
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xl font-bold text-blue-800">{formatMetric(yourRanking)}</p>
            <p className="text-xs text-slate-600">{yourRanking.eligible_quotations} eligible quotations</p>
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-md border border-slate-200 bg-white">
        {loading ? (
          <div className="flex h-64 items-center justify-center text-slate-500">
            <RefreshCw size={20} className="mr-2 animate-spin" /> Loading rankings...
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-center text-slate-500">
            <Trophy size={32} className="mb-3 text-slate-300" />
            <p className="font-medium text-slate-700">No qualifying activity in this period.</p>
            <p className="mt-1 text-sm">Rankings update as eligible quotations are submitted.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="w-20 px-4 py-3">Rank</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Membership</th>
                  <th className="px-4 py-3">{selectedCategory.label}</th>
                  <th className="px-4 py-3">Quotations</th>
                  <th className="w-32 px-4 py-3">Change</th>
                  <th className="w-44 px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.company_id} className={row.company_id === member?.company_id ? 'bg-blue-50/60' : 'hover:bg-slate-50'}>
                    <td className="px-4 py-4">
                      {row.rank ? (
                        <span className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-sm font-bold ${rankClasses[row.rank] || 'border-slate-200 bg-white text-slate-700'}`}>#{row.rank}</span>
                      ) : <span className="text-slate-400">N/A</span>}
                    </td>
                    <td className="px-4 py-4">
                      <button type="button" onClick={() => openProfile(row)} className="flex items-center gap-3 text-left hover:text-blue-700">
                        <img
                          src={getLogoOrInitials(row, row.company_name)}
                          alt=""
                          className="h-10 w-10 rounded-full border border-slate-200 object-cover"
                        />
                        <span className="font-semibold text-slate-900">{row.company_name}</span>
                      </button>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">{row.country || 'Not provided'}</td>
                    <td className="px-4 py-4"><Badge variant="outline">{row.tier || 'Member'}</Badge></td>
                    <td className="px-4 py-4 font-semibold text-slate-900">{formatMetric(row)}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{row.eligible_quotations}</td>
                    <td className="px-4 py-4 text-sm">
                      {row.rank_change > 0 && <span className="inline-flex items-center text-green-700"><ArrowUp size={14} className="mr-1" />{row.rank_change}</span>}
                      {row.rank_change < 0 && <span className="inline-flex items-center text-red-700"><ArrowDown size={14} className="mr-1" />{Math.abs(row.rank_change)}</span>}
                      {row.rank_change === 0 && <span className="text-slate-500">No change</span>}
                      {row.rank_change == null && <span className="text-slate-400">New</span>}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openProfile(row)} title="View company profile">
                          <Building2 size={15} />
                        </Button>
                        {payload?.can_message && row.company_id !== member?.company_id && (
                          <Button variant="outline" size="sm" onClick={() => openMessage(row)} title="Message company">
                            <MessageSquare size={15} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {payload?.preview && (
        <section className="flex flex-col items-start justify-between gap-4 border border-blue-200 bg-blue-50 p-5 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-semibold text-slate-900">{payload.upgrade_message}</h2>
            <p className="mt-1 text-sm text-slate-600">Paid members can view every company, all periods, and their own ranking.</p>
          </div>
          <Button onClick={() => navigate('/upgrade')} className="bg-blue-600 text-white hover:bg-blue-700">
            View Memberships <ArrowRight size={16} className="ml-2" />
          </Button>
        </section>
      )}
    </div>
  );
};

export default MemberLeaderboard;
